import type { ApolloClient, InMemoryCache } from "@apollo/client"
import { useLazyQuery, useMutation } from "@apollo/client"
import { usePathname } from "next/navigation"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import {
  accessTokenHasExpired,
  createRequestContext,
  getClient,
} from "lib/apolloClient"
import { FeatureFlagContext } from "components/FeatureFlagProvider"
import * as GQL from "./queries"
import { datadogLogs } from "@datadog/browser-logs"
import { useUser } from "lib/user"
import { activeMemberships } from "lib/apollo/constants"
import { useUserContext } from "components/UserProvider"

type Prompts = string[] | "SYNC" | "FAILURE" | undefined
type UseUserPrompt = {
  shouldShowPrompt: (
    prompt: string,
    featureFlag?: { name: string; requiredValue: string | number | boolean }
  ) => boolean
  markPromptAsSeen: (prompt: string) => Promise<void>
}

const USER_PROMPTS_STORAGE_KEY = "UserPrompts"
const PAGE_EXCLUSIONS = [/\/(us\/)?(basket|checkout|login|register).*?/]
export const MEMBERSHIP_SKUS = {
  membershipsWithPerks: [
    "BP_PLUS_UK",
    "BP_PLUS_US",
    activeMemberships["en-GB"].plusMembershipId,
    activeMemberships["en-US"].plusMembershipId,
  ],
  freeTrial: [
    "BP_TRIAL_60D_UK",
    "BP_TRIAL_60D_US",
    activeMemberships["en-GB"].sixtyDayFreeTrialRenewsToAnnualMembershipId,
    activeMemberships["en-US"].sixtyDayFreeTrialRenewsToAnnualMembershipId,
  ],
}
export const status = { assessed: false }

function fromLocalStorage(): Prompts {
  const value = window.localStorage.getItem(USER_PROMPTS_STORAGE_KEY)
  if (!value) return undefined
  try {
    return JSON.parse(value)
  } catch (_) {
    return value as Prompts
  }
}

function toLocalStorage(currentPrompts: Prompts): void {
  if (currentPrompts) {
    window.localStorage.setItem(
      USER_PROMPTS_STORAGE_KEY,
      currentPrompts && Array.isArray(currentPrompts)
        ? JSON.stringify([...new Set(currentPrompts)])
        : currentPrompts ?? "SYNC"
    )
  }
}

const apolloClientOptions = () => ({
  client: getClient() as ApolloClient<InMemoryCache>,
  context: createRequestContext({
    sessionStrategy: "START_NEW_ANONYMOUS",
  }),
})

/* HOOK */
export const useUserPrompt = (): UseUserPrompt => {
  const { isLoggedIn, membership } = useUser()
  const { ldClient } = useContext(FeatureFlagContext)
  const { type: memberType } = useUserContext()
  const pathName = usePathname()
  const [currentPrompts, setCurrentPrompts] = useState<Prompts>()

  const [getPromptsQuery, getPromptsState] = useLazyQuery(
    GQL.GET_USER_PROMPTS,
    apolloClientOptions()
  )
  const [addPromptMutation] = useMutation(
    GQL.ADD_UPS_USER_PROMPT,
    apolloClientOptions()
  )

  // Truthy evaluators
  const hasMembershipWithPerks = useMemo(
    () =>
      !!membership?.id &&
      MEMBERSHIP_SKUS.membershipsWithPerks.includes(membership.id),
    [membership?.id]
  )
  const hasFreeTrialMembership = useMemo(
    () => !!membership?.id && MEMBERSHIP_SKUS.freeTrial.includes(membership.id),
    [membership?.id]
  )
  const isInGracePeriod = useMemo(() => {
    if (typeof window === "undefined") return false
    return !!window?.sessionStorage?.getItem("failed-renewal-modal")
  }, [])

  const promptNotSeenBefore = useCallback(
    (prompt: string): boolean => {
      return Array.isArray(currentPrompts) && !currentPrompts.includes(prompt)
    },
    [currentPrompts]
  )

  const isTargetedUserContext = useCallback((): boolean => {
    return (
      isLoggedIn &&
      (hasFreeTrialMembership ||
        hasMembershipWithPerks ||
        memberType === "lapsedmember")
    )
  }, [isLoggedIn, hasFreeTrialMembership, hasMembershipWithPerks, memberType])

  const isNotExcludedPage = useCallback((): boolean => {
    return PAGE_EXCLUSIONS.every((exclusion) => !pathName.match(exclusion))
  }, [pathName])

  useEffect(() => {
    if (!status.assessed) {
      const lsPrompts = fromLocalStorage()
      if (isTargetedUserContext()) {
        if (accessTokenHasExpired()) {
          setCurrentPrompts("SYNC")
        } else if (Array.isArray(lsPrompts)) {
          setCurrentPrompts(lsPrompts)
        } else if (lsPrompts !== "FAILURE") {
          if (
            isNotExcludedPage() &&
            !getPromptsState.loading &&
            !getPromptsState.error
          ) {
            getPromptsQuery()
          }
        }
      } else if (lsPrompts) {
        localStorage.removeItem(USER_PROMPTS_STORAGE_KEY)
      }
      status.assessed = true
    }
  }, [
    getPromptsQuery,
    isNotExcludedPage,
    getPromptsState,
    isTargetedUserContext,
  ])

  useEffect(() => toLocalStorage(currentPrompts), [currentPrompts])

  useEffect(() => {
    if (getPromptsState?.data || getPromptsState?.error) {
      const remotePrompts = getPromptsState?.data?.me?.profile?.prompts
      setCurrentPrompts(
        Array.isArray(remotePrompts) ? remotePrompts : "FAILURE"
      )
    }
  }, [getPromptsState?.data, getPromptsState?.error])

  useEffect(() => toLocalStorage(currentPrompts), [currentPrompts]) // Sync local storage against updated state

  // Utility functions returned by the hook
  const markPromptAsSeen: UseUserPrompt["markPromptAsSeen"] = useCallback(
    async (prompt) => {
      setCurrentPrompts(
        Array.isArray(currentPrompts) ? [...currentPrompts, prompt] : [prompt]
      )
      try {
        await addPromptMutation({ variables: { prompt } })
      } catch (err) {
        datadogLogs.logger.error("Error updating user prompts in UPS", {
          err,
          prompt,
        })
      }
    },
    [addPromptMutation, currentPrompts]
  )

  const shouldShowPrompt: UseUserPrompt["shouldShowPrompt"] = useCallback(
    (prompt, featureFlagCondition) => {
      if (isInGracePeriod) {
        return false
      }

      const show =
        promptNotSeenBefore(prompt) &&
        isNotExcludedPage() &&
        isTargetedUserContext()

      if (show && featureFlagCondition) {
        const ffValue = ldClient?.variation(featureFlagCondition.name)
        return show && ffValue === featureFlagCondition.requiredValue
      }

      return show
    },
    [
      promptNotSeenBefore,
      isNotExcludedPage,
      isTargetedUserContext,
      ldClient,
      isInGracePeriod,
    ]
  )

  return {
    shouldShowPrompt,
    markPromptAsSeen,
  }
}
